import { useCallback } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "@mui/material";
import TaskSelector from "src/business-components/input/TaskSelector/TaskSelector";
import { ALL_TASKS } from "src/business-components/input/TaskSelector/TaskSelector.constants";
import { useTaskSelector } from "src/business-components/input/TaskSelector/TaskSelector.hooks";
import type { TaskSelectorOptionOnClickType } from "src/business-components/input/TaskSelector/TaskSelector.types";
import PopperDropdown from "src/components/general/PopperDropdown/PopperDropdown";
import { withInlineAutocomplete } from "src/components/input/withInlineAutocomplete/withInlineAutocomplete";
import { useBuildLink } from "src/hooks/useBuildLink";
import type {
  AppPath,
  PitPath,
  TaskPath,
  TaskRunPath,
} from "src/utils/appRoutes/appRoutes.types";
import { TaskIcon } from "@ui-icons";
import BreadcrumbLink from "../BreadcrumbLink/BreadcrumbLink";
import BreadcrumbPopperArrow from "../BreadcrumbPopperArrow/BreadcrumbPopperArrow";

const InlineTaskSelector = withInlineAutocomplete(TaskSelector);

// Avoid `} as` / `<` inside .tsx in positions where the TSX parser mis-reads.
function buildPathForTaskOption(
  buildLink: ReturnType<typeof useBuildLink>,
  option: { task_name?: string },
  app_name: string,
  app_pit: string | undefined,
  getTaskRunByName: (name: string) => { task_id: string } | undefined
): string | undefined {
  const search = window.location.search;

  if (option.task_name === ALL_TASKS) {
    return buildLink(
      app_pit
        ? ({ app_name, app_pit, search } as PitPath)
        : ({ app_name, search } as AppPath)
    );
  }
  const taskRun = getTaskRunByName(option.task_name);
  if (app_pit && taskRun) {
    return buildLink({
      app_name,
      app_pit,
      task_id: taskRun.task_id,
      search,
    } as TaskRunPath);
  }
  if (option.task_name) {
    return buildLink({ app_name, task_name: option.task_name, search } as TaskPath);
  }
  return undefined;
}

interface BreadcrumbTaskLinkProps {
  app_name: string;
  app_pit?: string;
  task_name?: string;
  isLoading?: boolean;
}

const BreadcrumbTaskLink = ({
  app_name,
  task_name,
  app_pit,
  isLoading,
}: BreadcrumbTaskLinkProps) => {
  const {
    task,
    tasks,
    isLoading: isLoadingTasks,
    getTaskRunByName,
  } = useTaskSelector({
    app_pit,
    task_name,
    app_name,
    alwaysShowAllTasksOption: false,
  });
  const buildLink = useBuildLink();
  const navigate = useNavigate();

  const onClickRenderOption: TaskSelectorOptionOnClickType = useCallback(
    (_e, option) => {
      const url = buildPathForTaskOption(
        buildLink,
        option,
        app_name,
        app_pit,
        getTaskRunByName
      );
      if (url) {
        void navigate(url);
      }
    },
    [navigate, app_pit, getTaskRunByName, app_name, buildLink]
  );

  const arrowMode = !!(app_pit && task_name);

  return (
    <PopperDropdown
      slotProps={{
        popper: {
          disablePortal: true,
        },
      }}
      content={({ hide }) => (
        <InlineTaskSelector
          autoFocus
          isLoading={isLoadingTasks}
          task={task}
          options={tasks}
          slotProps={{
            renderOption: {
              onClick: (e, o) => {
                hide();
                onClickRenderOption(e, o);
              },
              app_pit,
            },
          }}
        />
      )}
    >
      {({ isOpen, anchorRef, handleOnMouseDownCapture, toggleAndPrevent }) => (
        <BreadcrumbLink
          ref={anchorRef}
          data-test-id="breadcrumb-task"
          className="text-xs"
          isLoading={isLoading}
          to={
            task_name
              ? buildLink({
                  app_name,
                  task_name,
                  search: window.location.search,
                } as TaskPath)
              : undefined
          }
          startAdornment={TaskIcon}
          onClick={arrowMode ? undefined : toggleAndPrevent}
          onMouseDownCapture={arrowMode ? undefined : handleOnMouseDownCapture}
          endAdornment={
            // Only show arrow if no task is selected or there are multiple tasks
            !task_name || tasks.length > 1 ? (
              <BreadcrumbPopperArrow
                isOpen={isOpen}
                onClick={toggleAndPrevent}
                onMouseDownCapture={handleOnMouseDownCapture}
              />
            ) : undefined
          }
          tooltip={
            isOpen
              ? undefined
              : task_name && (
                  <div className="flex flex-col gap-1">
                    <span>{task_name}</span>
                  </div>
                )
          }
          TooltipComponent={Tooltip}
        >
          <div className="truncate">{task_name ?? ALL_TASKS}</div>
        </BreadcrumbLink>
      )}
    </PopperDropdown>
  );
};

export default BreadcrumbTaskLink;
